import { useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useHistory } from 'hooks/generic/useHistory';
import { getPath } from 'utils/navigation/navigate';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';
import { Configs } from 'constants/app/configs';
import {
  ELsKeyAuthReachedFromOptions,
  ELsKeys
} from 'constants/app/storage-keys';
import { ViewModes } from 'interfaces';

export const useOpenAccountAuth = () => {
  const { push, goBack } = useHistory();
  const _isLoginRoute = useRouteMatch({
    path: ViewModes.DEFAULT
      ? EAccountRouteKeys.ACCOUNT_AUTH
      : EAccountRouteKeys.ACCOUNT_LOGIN
  });

  const _isRegisterRoute = useRouteMatch({
    path: ViewModes.DEFAULT
      ? EAccountRouteKeys.ACCOUNT_AUTH
      : EAccountRouteKeys.ACCOUNT_REGISTER
  });

  const openAccountLoginModal = useCallback(
    (isFromCartAuth?: boolean) => {
      switch (Configs.VIEW_MODE) {
        case ViewModes.DEFAULT:
          push(EAccountRouteKeys.ACCOUNT_AUTH);
          break;
        case ViewModes.FTNFT_AM:
          if (isFromCartAuth) {
            push(EAccountRouteKeys.ACCOUNT_AUTH);
          } else {
            push(EAccountRouteKeys.ACCOUNT_LOGIN);
          }
          break;
        case ViewModes.KIOSK:
        case ViewModes.KIOSK_AM:
        case ViewModes.KIOSK_VENICE:
        default:
          push(EAccountRouteKeys.ACCOUNT_LOGIN);
          break;
      }
    },
    [push]
  );

  const openAccountLogin = useCallback(() => {
    if (Configs.PAGE_URLS.LOGIN) {
      const authReachedFrom = JSON.parse(
        JSON.stringify(localStorage.getItem(ELsKeys.AUTH_REACHED_FROM)) ||
          'null'
      );
      if (authReachedFrom === ELsKeyAuthReachedFromOptions.CREATE_NFT) {
        openAccountLoginModal();
      } else {
        window.location.href = getPath(Configs.PAGE_URLS.LOGIN);
        // returning true for indicating redirecting to another page
        return true;
      }
    }
    openAccountLoginModal();
  }, [openAccountLoginModal]);

  const openAccountRegisterModal = useCallback(() => {
    switch (Configs.VIEW_MODE) {
      case ViewModes.DEFAULT:
        push(EAccountRouteKeys.ACCOUNT_AUTH);
        break;
      case ViewModes.KIOSK:
      case ViewModes.FTNFT_AM:
      case ViewModes.KIOSK_AM:
      case ViewModes.KIOSK_VENICE:
      default:
        push(EAccountRouteKeys.ACCOUNT_REGISTER);
        break;
    }
  }, [push]);

  const openAccountRegister = useCallback(() => {
    if (Configs.PAGE_URLS.REGISTER) {
      window.location.href = getPath(Configs.PAGE_URLS.REGISTER);
      // returning true for indicating redirecting to another page
      return true;
    }
    openAccountRegisterModal();
  }, [openAccountRegisterModal]);

  const openAccountAuth = useCallback(() => {
    push(EAccountRouteKeys.ACCOUNT_AUTH);
  }, [push]);

  const openAccountResetPassword = useCallback(() => {
    switch (Configs.VIEW_MODE) {
      case ViewModes.DEFAULT:
      case ViewModes.KIOSK:
      case ViewModes.KIOSK_AM:
      case ViewModes.KIOSK_VENICE:
        break;
      case ViewModes.FTNFT_AM:
        push(EAccountRouteKeys.ACCOUNT_RESET_PASSWORD);
        break;
      default:
        break;
    }
  }, [push]);

  const openAccountForgotPassword = useCallback(() => {
    switch (Configs.VIEW_MODE) {
      case ViewModes.DEFAULT:
      case ViewModes.KIOSK:
      case ViewModes.KIOSK_AM:
      case ViewModes.KIOSK_VENICE:
        break;
      case ViewModes.FTNFT_AM:
        push(EAccountRouteKeys.ACCOUNT_FORGOT_PASSWORD);
        break;
      default:
        break;
    }
  }, [push]);

  return {
    openAccountLogin,
    openAccountLoginModal,
    openAccountRegister,
    openAccountRegisterModal,
    openAccountAuth,
    openAccountResetPassword,
    openAccountForgotPassword,
    goBack,
    isLoginRoute: !!_isLoginRoute,
    isRegisterRoute: !!_isRegisterRoute
  };
};
